// Copyright (c) Docugami, Inc. All rights reserved.

import { IconAlignment } from "components/cards/CommandButton";
import { DocumentNavButton, nextDocumentNavProps, prevDocumentNavProps } from "components/common/DocumentNavButton";
import { WebDocHost } from "components/doc_hosting/doc_hosts/web/presentation/WebDocHost";
import { PagesFilmStrip } from "components/doc_hosting/presentation/PagesFilmStrip";
import { emptyGray } from "components/shell/presentation/AppStyles";
import { forwardRef, Ref, useState } from "react";
import styled from "styled-components";
import { StepCanvas } from "./WorkPathStyles";

const DocumentContainer = styled.div`
    flex: 1;
    display: flex;
    justify-content: center;
    overflow: hidden;
`

const CanvasContainer = styled(StepCanvas) <{ emptyBg: boolean }>`
    overflow-x: auto;
    flex: auto;
    ${props => props.emptyBg && "background-color: transparent"};
    height: 100%;
`;

const FilmStripContainer = styled.div`
    width: 60px;
    border-right: 1px solid ${emptyGray};
`;

interface FilmStripProps {
    documentId: string;
}

interface DocumentViewCanvasProps {
    documentId: string;
    docSetId: string;
    isFilePreview?: boolean;
    className?: string;
    showNavigation?: boolean;
    onNavigatePrev?: () => void;
    onNavigateNext?: () => void;
}

export const FilmStrip = (props: FilmStripProps) => {
    return (
        <FilmStripContainer>
            <PagesFilmStrip documentId={props.documentId} />
        </FilmStripContainer>
    );
}

/**
 * Wrapper component for properly displaying the SDV within a scrollable panel
 * @param props
 */
export const DocumentViewCanvas = forwardRef((props: DocumentViewCanvasProps, ref?: Ref<HTMLDivElement>): JSX.Element => {
    const [propertiesHidden, setPropertiesHidden] = useState(true);

    return (
        <CanvasContainer ref={ref} emptyBg={!!props.isFilePreview} className={props.className}>
            <FilmStrip documentId={props.documentId} />
            <DocumentContainer>
                {
                    !!props.showNavigation && (
                        <DocumentNavButton
                            label={prevDocumentNavProps.label}
                            iconName={prevDocumentNavProps.iconName}
                            iconAlignment={IconAlignment.Left}
                            onClick={() => props.onNavigatePrev?.()}
                            disabled={!props.onNavigatePrev} />
                    )
                }
                <WebDocHost
                    documentId={props.documentId}
                    clusterId={props.docSetId}
                    isFilePreview={props.isFilePreview}
                    propertiesHidden={propertiesHidden}
                    setPropertiesHidden={setPropertiesHidden} />
                {
                    !!props.showNavigation && (
                        <DocumentNavButton
                            label={nextDocumentNavProps.label}
                            iconName={nextDocumentNavProps.iconName}
                            iconAlignment={IconAlignment.Right}
                            onClick={() => props.onNavigateNext?.()}
                            disabled={!props.onNavigateNext} />
                    )
                }
            </DocumentContainer>
        </CanvasContainer>
    );
});
